<template>
  <v-card
    class="rounded"
    :class="{ 'py-2 px-4': dense, 'pa-4': !dense }"
    width="100%"
    flat
    :disabled="disabled"
    :ripple="false"
    @click="handleClick"
  >
    <div class="full-width d-flex justify-space-between align-center">
      <div class="d-flex align-center text-left">
        <Icon :icon="icon" :tile="iconTile" :color="color" class="mr-4" />
        <div class="d-flex flex-column">
          <Typography variant="subtitle-1" as="span" class="word-break">{{ title }}</Typography>
          <Typography
            v-if="subtitle"
            variant="body-2"
            as="span"
            class="word-break"
            :class="subtitleTextClass"
            >{{ subtitle }}</Typography
          >
        </div>
      </div>

      <v-progress-circular v-if="loading" indeterminate color="brandPrimary" width="2" size="25" />
      <Typography
        v-else-if="textRight"
        variant="subtitle-1"
        as="span"
        class="word-break text-right"
        >{{ textRight }}</Typography
      >
      <Icon v-else-if="rightIcon" :icon="rightIcon" :color="iconRightColor" class="mr-2" />
    </div>
    <!-- Expandable content slot. Controlled rows can show content with the chevron off. -->
    <v-expand-transition v-if="expandable || controlExpansion">
      <div v-if="isExpanded">
        <slot name="expanded-content"></slot>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
// Components
import Typography from "@/components/generic/Typography.vue";
import Icon from "@/components/generic/Icon.vue";

export default {
  name: "WalletTileItem",
  emits: ["click", "expand"],
  components: {
    Typography,
    Icon,
  },
  props: {
    icon: {
      type: String,
      required: true,
      validator: (value) => value.includes("mdi-"),
    },
    iconTile: {
      type: Boolean,
      default: true,
    },
    color: {
      type: String,
      default: "systemPrimary",
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
      default: null,
    },
    subtitleTextClass: {
      type: [String, Object],
      required: false,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    iconRight: {
      type: String,
      required: false,
      default: null,
      validator: (value) => !value || value.includes("mdi-"),
    },
    iconRightColor: {
      type: String,
      required: false,
      default: "black",
    },
    textRight: {
      type: String,
      required: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: false,
    },
    // When true, `expanded` owns the chevron. A click emits `click` only
    // (not `expand`) and does not toggle the row, so the parent can keep
    // the chevron and the card on the same flag.
    controlExpansion: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalExpanded: this.defaultExpanded,
    };
  },
  computed: {
    isExpanded: {
      get() {
        return this.controlExpansion ? this.expanded : this.internalExpanded;
      },
      set(value) {
        if (!this.controlExpansion) this.internalExpanded = value;
      },
    },
    rightIcon() {
      if (this.expandable) {
        return this.isExpanded ? "mdi-chevron-up" : "mdi-chevron-down";
      }
      return this.iconRight || null;
    },
  },
  watch: {
    // Hosts often resolve this after mount, so data() alone would miss it.
    defaultExpanded(value) {
      this.isExpanded = value;
    },
  },
  methods: {
    handleClick() {
      if (this.expandable && !this.controlExpansion) {
        this.toggleExpand();
      } else {
        this.$emit("click");
      }
    },
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      this.$emit("expand", this.isExpanded);
      this.$emit("click");
    },
  },
};
</script>

<style scoped>
/* Hides gray background when clicking v-card */
.v-card--link:before {
  background: none;
}
</style>
